import Layout from 'components/layout/Layout'
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations'
import i18nConfig from 'utils/i18nConfig'
import { DEFAULT_NAMESPACES, SIMULATION_NAMESPACES } from 'constants/i18n'
import { cmsLib } from 'lib'
import { faqCategoriesSlugs } from 'constants/faq-categories'
import { FAQCategories } from 'features/faq-page/types'
import { BoltDriveFlexProps } from 'features/bolt-drive-flex/types'
import BoltDriveFlex from 'features/bolt-drive-flex/BoltDriveFlex'
import { useStoreDispatch } from 'hooks/store'
import useSelectInputGroup from 'hooks/useSelectInputGroup'
import { updateMarketingData } from 'store/marketing/slice'
import { MarketingDataKeys } from 'store/marketing/types'
import { EV_FLEX_PROMO_CODE, EV_FLEX_SIMULATION_SO } from 'features/bolt-drive-flex/BoltDriveFlex.constants'
import { updateInput } from 'store/customer/slice'
import { InputGroupKeys, MeterDetailsFieldKeys } from 'store/customer/enums'
import { setPromoOrReferralCodeInStore } from 'utils/customerFlow'
import { COUNTER_TYPES } from '@boltenergy-be/shared'
import { SeoProps } from 'components/seo/types'
import { useTranslation } from 'next-i18next/pages'

const DriveFlexPage = (props: Omit<BoltDriveFlexProps, 'extraStartSimulationAction'>) => {
  // Redux
  const { counterType, dynamicTariff } = useSelectInputGroup(InputGroupKeys.METER_DETAILS)
  const dispatch = useStoreDispatch()

  // i18n
  const { t } = useTranslation('electricVehicles')

  // Constants
  const seo: SeoProps = {
    title: t('seo.title'),
    description: t('seo.description')
  }

  /**
   * Update marketing data, update dynamic tariff field and set promo code before starting the simulation
   */
  const extraStartSimulationAction = async () => {
    // Set simulation SO
    dispatch(updateMarketingData({ key: MarketingDataKeys.SIMULATION_SO, value: EV_FLEX_SIMULATION_SO }))

    // Set dynamic tariff to true
    if (dynamicTariff !== true) {
      dispatch(
        updateInput({
          group: InputGroupKeys.METER_DETAILS,
          key: MeterDetailsFieldKeys.DYNAMIC_TARIFF,
          value: true
        })
      )
    }

    // Set counter type to digital if not already set
    if (counterType !== COUNTER_TYPES.digital) {
      dispatch(updateInput({ group: InputGroupKeys.METER_DETAILS, key: MeterDetailsFieldKeys.COUNTER_TYPE, value: COUNTER_TYPES.digital }))
    }

    // add promo code to store
    await setPromoOrReferralCodeInStore(EV_FLEX_PROMO_CODE)
  }

  return (
    <Layout {...{ extraStartSimulationAction, seo }}>
      <BoltDriveFlex {...{ extraStartSimulationAction }} {...props} />
    </Layout>
  )
}

export const getStaticProps = async ({ locale }) => {
  const categories = await cmsLib.getFaqCategories(faqCategoriesSlugs[FAQCategories.BOLT_DRIVE_FLEX], locale)

  return {
    props: {
      faqCategories: categories,
      ...(await serverSideTranslations(locale, [...DEFAULT_NAMESPACES, 'electricVehicles', ...SIMULATION_NAMESPACES], i18nConfig))
    },
    revalidate: 600
  }
}

export default DriveFlexPage
